import React from 'react';
import { TextField } from 'components/mui';
import { Autocomplete } from '@mui/material';

const dropdownBelow = {
	popper: {
		placement: 'bottom-start' as const,
		disablePortal: true,
		modifiers: [{ name: 'flip', enabled: false }],
	},
	listbox: { sx: { maxHeight: 160 } },
};

/** Ensures the current value is always present in options so MUI Autocomplete displays it. */
export function ensureOption<T extends { Id: number }>(options: T[], current: T | null): T[] {
	if (!current) return options;
	if (options.some((o) => o.Id === current.Id)) return options;
	return [current, ...options];
}

interface FormAutocompleteProps<T> {
	label: string;
	options: T[];
	value: T | null;
	onChange: (_event: unknown, value: T | null) => void;
	getOptionLabel: (option: T) => string;
	onSearch: (query: string) => void;
	isOptionEqualToValue?: (option: T, value: T) => boolean;
	disabled?: boolean;
	error?: boolean;
	helperText?: string;
	required?: boolean;
}

function FormAutocomplete<T extends { Id: number }>({
	label,
	options,
	value,
	onChange,
	getOptionLabel,
	onSearch,
	isOptionEqualToValue,
	disabled,
	error,
	helperText,
	required,
}: FormAutocompleteProps<T>) {
	return (
		<Autocomplete
			slotProps={dropdownBelow}
			options={options}
			getOptionLabel={getOptionLabel}
			value={value}
			onChange={onChange}
			onInputChange={(_e, val) => onSearch(val)}
			isOptionEqualToValue={isOptionEqualToValue ?? ((opt, val) => opt.Id === val.Id)}
			disabled={disabled}
			renderInput={(params) => (
				<TextField
					{...params}
					label={required ? `${label} *` : label}
					error={error}
					helperText={helperText}
					size="small"
				/>
			)}
			onOpen={() => onSearch('')}
		/>
	);
}

interface FormAutocompleteMultiProps<T> {
	label: string;
	options: T[];
	value: T[];
	onChange: (_event: unknown, value: T[]) => void;
	getOptionLabel: (option: T) => string;
	onSearch: (query: string) => void;
	isOptionEqualToValue?: (option: T, value: T) => boolean;
}

export function FormAutocompleteMulti<T extends { Id: number }>({
	label,
	options,
	value,
	onChange,
	getOptionLabel,
	onSearch,
	isOptionEqualToValue,
}: FormAutocompleteMultiProps<T>) {
	return (
		<Autocomplete
			slotProps={dropdownBelow}
			multiple
			options={options}
			getOptionLabel={getOptionLabel}
			value={value}
			onChange={onChange}
			onInputChange={(_e, val) => onSearch(val)}
			isOptionEqualToValue={isOptionEqualToValue ?? ((opt, val) => opt.Id === val.Id)}
			renderInput={(params) => (
				<TextField {...params} label={label} size="small" />
			)}
			onOpen={() => onSearch('')}
		/>
	);
}

export default FormAutocomplete;
